<template>
  <div class="tag_container">
    <div
      class="sitemap_tag"
      @mouseenter="showTooltip"
      @mouseleave="hideTooltip"
    >
      {{ assignedKeyword.keyword }}
      <svg-vue
        v-if="(isEditingAndOnAiOnboarding || !isOnboarding) && assignedKeyword.query_type"
        class="sitemap_icon tooltipster"
        :icon="queryTypeIcon"
        :title="`Keyword Intent: ${assignedKeyword.query_type}`"
      />
      <svg-vue
        v-if="(isEditingAndOnAiOnboarding || !isOnboarding) && assignedKeyword.ranking_mismatch"
        class="sitemap_icon tooltipster mismatch"
        icon="icons/link-break"
        :title="`This keyword ranks ${assignedKeyword.ranking_position} for ${assignedKeyword.ranking_page}`"
      />
      <svg-vue
        v-if="(isEditingAndOnAiOnboarding || !isOnboarding) && assignedKeyword.auto_assigned"
        class="sitemap_icon tooltipster"
        icon="icons/star"
        title="Auto-assigned"
      />
      <svg-vue
        v-if="isEditingAndOnAiOnboarding || !isOnboarding"
        class="sitemap_icon tooltipster remove_keyword"
        icon="icons/cross"
        title="Remove keyword"
        @click="removeKeyword()"
      />
    </div>
    <Transition>
      <div
        v-if="!isOnboarding && hover"
        class="tag_tooltip_wrapper"
        @mouseenter="showTooltip"
        @mouseleave="hideTooltip"
      >
        <div class="tag_tooltip">
          <table>
            <tbody>
              <tr>
                <td>Difficulty:</td>
                <td>
                  <difficulty-tag :difficulty="assignedKeyword.difficulty ?? null" />
                </td>
              </tr>
              <tr>
                <td>Search Volume:</td>
                <td>{{ assignedKeyword.search_volume ? assignedKeyword.search_volume.toLocaleString() : '< 10' }}</td>
              </tr>
              <tr>
                <td>Keyword Intent:</td>
                <td>{{ assignedKeyword.query_type }}</td>
              </tr>
              <tr>
                <td>Ranking Page:</td>
                <td>
                  <div class="ranking_mismatch_row">
                    <div class="marquee">
                      <a
                        :href="`${websiteDomain}${assignedKeyword.ranking_page}`"
                        target="_blank"
                        class="marquee_inner"
                      >
                        {{ assignedKeyword.ranking_page }}
                      </a>
                    </div>
                    <svg-vue
                      v-if="assignedKeyword.ranking_mismatch"
                      :title="`This keyword ranks ${assignedKeyword.ranking_position} for ${assignedKeyword.ranking_page}`"
                      icon="icons/link-break"
                      class="sitemap_icon tooltipster mismatch"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>Ranking Position:</td>
                <td>{{ assignedKeyword.ranking_position }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import DifficultyTag from "../../global/assets/DifficultyTag";

export default {
  components: {DifficultyTag},
  props: {
    page: {
      type: Object,
      required: true,
    },
    assignedKeyword: {
      type: Object,
      required: true,
    },
    isOnboarding: {
      required: false,
      type: Boolean,
      default: false,
    }
  },
  data(){
    return {
      hover: false,
      hideTimeout: null,
    }
  },
  computed: {
    ...mapGetters('strategy', [
      'websiteDomain',
    ]),
    ...mapState('strategy', [
      'isEditing',
    ]),
    queryTypeIcon(){
      switch (this.assignedKeyword?.query_type) {
        case 'Transactional':
          return 'icons/shopping-cart';
        case 'Informational':
          return 'icons/info-circle';
        case 'Mixed':
          return 'icons/question_mark_in_circle';
        default:
          return '';
      }
    },
    isEditingAndOnAiOnboarding() {
      return this.isEditing && this.isOnboarding;
    }
  },
  methods: {
    ...mapActions('strategy', [
        'removeKeywordFromPage',
    ]),
    showTooltip() {
      clearTimeout(this.hideTimeout);
      this.hover = true;
    },
    hideTooltip(){
      this.hideTimeout = setTimeout(() => {
        this.hover = false;
      }, 250);
    },
    removeKeyword() {
      let {page, assignedKeyword} = this;
      this.removeKeywordFromPage({page, assignedKeyword})
    },
  }
}
</script>

<style lang="scss" scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.tag_container {
  position: relative;

  .sitemap_tag {
    background-color: #F4FEFA;
    border: 1px solid #DFF5EE;
    border-radius: 6px;
    padding: 4px 8px 4px 8px;
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: -0.1px;
    text-align: left;
    margin: 0;
    display: flex;
    align-items: center;
    transition: .3s;

    .remove_keyword {
      cursor: pointer;
      transition: .3s;
    }
  }

  .tag_tooltip {
    z-index: 10;
    position: absolute;
    bottom: 100%;
    margin-bottom: .5rem;
    background: #FFFFFF;
    border: 1px solid #C7C4C1;
    border-radius: 12px;
    padding: 24px;

    table {
      width: 100%;

      tr td {
        font-size: 0.8125rem;
        font-weight: 400;
        line-height: 22px;
        letter-spacing: -0.1px;
        text-align: left;
        overflow: hidden;
        white-space: nowrap;
        max-width: 130px;
      }

      tr td:first-of-type {
        font-weight: 500;
        padding-right: 16px;
      }

      .ranking_mismatch_row {
        display: flex;
        align-items: center;
        max-width: 130px;

        .marquee {
          max-width: 116px;
          position: relative;
          overflow: hidden;
          --move-initial: 0%;
          --move-final: -100%;

          .marquee_inner {
            width: fit-content;
            display: flex;
            position: relative;

            &:hover {
              transform: translate3d(var(--move-initial), 0, 0);
              animation: marquee 3s linear infinite;
            }
          }
        }
      }
    }
  }

  .sitemap_tag,
  .tag_tooltip {
    .sitemap_icon {
      height: 14px;
      width: 14px;
      margin-left: 5px;

      :deep(path) {
        &[stroke] {
          stroke: var(--primary-color);
        }

        &[fill] {
          fill: var(--primary-color);
        }
      }

      &.mismatch{
        :deep(path) {
          &[stroke] {
            stroke: var(--negative-color);
          }

          &[fill] {
            fill: var(--negative-color);
          }
        }
      }

      &.remove_keyword {
        :deep(path) {
          &[stroke] {
            stroke: var(--primary-font-color);
          }

          &[fill] {
            fill: var(--primary-font-color);
          }
        }
      }
    }
  }
}

@keyframes marquee {
  0% {
    transform: translate3d(var(--move-initial), 0, 0);
  }

  100% {
    transform: translate3d(var(--move-final), 0, 0);
  }
}
</style>
